import {assertAndContinue} from 'Asserts';
import {pluralize, sum} from 'BenchlingUtil';
import {DataType, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import aggregateRows from 'data-frames/util/legacy/aggregateRows';
import filterMaskedRows from 'data-frames/util/legacy/filterMaskedRows';
import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationErrorBarType as ErrorBarType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {CartesianPoint} from 'ui-library/charting/Types';

import {
  aggregateCount,
  aggregateMean,
  aggregateStandardDeviation,
  aggregateStandardError,
  aggregateSum,
} from './aggregationFunctions';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

const AGGREGATION_TYPE_TO_Y_FN: {[key in AggregationType]: (rows: Array<CartesianPoint>) => number | null} = {
  [AggregationType.COUNT]: (rows: Array<CartesianPoint>) => aggregateCount(rows, 'y'),
  [AggregationType.MEAN]: (rows: Array<CartesianPoint>) => aggregateMean(rows, 'y'),
  [AggregationType.SUM]: (rows: Array<CartesianPoint>) => aggregateSum(rows, 'y'),
};

const ERROR_BAR_TYPE_TO_ERROR_VALUE_FN: {
  [key in ErrorBarType]: (rows: Array<CartesianPoint>) => number | null;
} = {
  [ErrorBarType.STANDARD_DEVIATION]: (rows: Array<CartesianPoint>) => aggregateStandardDeviation(rows, 'y'),
  [ErrorBarType.STANDARD_ERROR]: (rows: Array<CartesianPoint>) => aggregateStandardError(rows, 'y'),
};

export function generateOutlierAggregationWarning(numOutliers: number): string {
  return `Aggregation includes ${numOutliers} ${pluralize(numOutliers, 'outlier')}`;
}

/**
 * Aggregate points with the same x-value into a single point, using the aggregationType (e.g. MEAN)
 * to calculate the y value and the errorBarType (e.g. STANDARD_ERROR) to calculate the errorValue.
 *
 * Masked points are excluded when calculating the aggregated points' y value and errorValue.
 *
 * If aggregationType is null, the original data frame is returned.
 *
 * @param dataFrame Data frame of CartesianPoints
 * @param aggregationType How to aggregate points. If null, the original data frame will be returned.
 * @param errorBarType What error bars should represent. If null, errorValue will not be calculated.
 */
export default function aggregateCartesianPoints(
  dataFrame: DeprecatedDataFrame<CartesianPoint>,
  aggregationType: AggregationType | null,
  errorBarType: ErrorBarType | null,
  outlierConfig: OutlierConfig | null
): DeprecatedDataFrame<CartesianPoint> {
  assertAndContinue(
    errorBarType == null || aggregationType != null,
    'Can only calculate error bar values for aggregated points'
  );

  // TODO(BNCH-24546): When series are stored in a data frame column, partition by both x and series
  if (aggregationType == null) {
    return dataFrame;
  } else {
    return aggregateRows(
      filterMaskedRows(dataFrame),
      (row) => String(row.x),
      (rows) => ({
        pointWarning: rows.some((row) => row.isOutlier === true)
          ? generateOutlierAggregationWarning(sum(rows.map((row) => row.isOutlier === true)))
          : null,
        x: rows[0].x, // Don't use spread, so aggregated points don't get extra values (like 'isMasked')
        y: AGGREGATION_TYPE_TO_Y_FN[aggregationType](rows),
        ...(errorBarType == null ? {} : {errorValue: ERROR_BAR_TYPE_TO_ERROR_VALUE_FN[errorBarType](rows)}),
        ...(dataFrame.axisTypes.isOutlier == null
          ? {}
          : {isOutlier: rows.some((row) => row.isOutlier === true)}),
      }),
      {
        pointWarning: DataType.STRING,
        x: dataFrame.axisTypes.x,
        y: dataFrame.axisTypes.y,
        ...(errorBarType == null ? {} : {errorValue: dataFrame.axisTypes.y}),
        ...(dataFrame.axisTypes.isOutlier ? {isOutlier: dataFrame.axisTypes.isOutlier} : {}),
      }
    );
  }
}
